import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, computed, effect, signal } from '@angular/core';

export type ThemePreference = 'light' | 'dark' | 'system';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly storageKey = 'themePreference';
  private readonly legacyDarkModeKey = 'darkMode';
  private readonly mediaQuery = this.getMediaQuery();
  private readonly systemPrefersDark = signal<boolean>(false);
  private readonly storageAvailable = this.checkStorageAvailable();

  readonly preference = signal<ThemePreference>('system');
  readonly effectiveTheme = computed<'light' | 'dark'>(() => {
    const preference = this.preference();
    if (preference === 'system') {
      return this.systemPrefersDark() ? 'dark' : 'light';
    }
    return preference;
  });

  private readonly mediaQueryListener = (event: MediaQueryListEvent | MediaQueryList) => {
    this.systemPrefersDark.set(
      'matches' in event ? event.matches : (this.mediaQuery?.matches ?? false)
    );
  };

  constructor(@Inject(DOCUMENT) private document: Document) {
    const storedPreference = this.getStoredPreference();
    if (storedPreference) {
      this.preference.set(storedPreference);
    }

    this.syncSystemPreference();
    this.registerMediaListener();

    effect(() => {
      const theme = this.effectiveTheme();
      const body = this.document?.body;
      if (!body) return;
      if (theme === 'dark') {
        body.classList.add('dark-mode');
      } else {
        body.classList.remove('dark-mode');
      }
    });
  }

  setPreference(preference: ThemePreference) {
    this.preference.set(preference);
    this.persistPreference(preference);
  }

  setSystemTheme() {
    this.setPreference('system');
  }

  setDarkTheme() {
    this.setPreference('dark');
  }

  setLightTheme() {
    this.setPreference('light');
  }

  private registerMediaListener() {
    if (!this.mediaQuery) return;
    const mq = this.mediaQuery as unknown as {
      addEventListener?: (type: 'change', listener: (event: MediaQueryListEvent) => void) => void;
      addListener?: (listener: (event: MediaQueryListEvent) => void) => void;
    };

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener(
        'change',
        this.mediaQueryListener as (event: MediaQueryListEvent) => void
      );
      return;
    }

    if (typeof mq.addListener === 'function') {
      // Legacy Safari
      mq.addListener(this.mediaQueryListener as (event: MediaQueryListEvent) => void);
    }
  }

  private syncSystemPreference() {
    if (!this.mediaQuery) return;
    this.systemPrefersDark.set(this.mediaQuery.matches);
  }

  private getStoredPreference(): ThemePreference | null {
    if (!this.isStorageAvailable()) return null;
    const stored = this.getStorageItem(this.storageKey);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }

    const legacy = this.getStorageItem(this.legacyDarkModeKey);
    if (legacy === '1' || legacy === '0') {
      const migrated: ThemePreference = legacy === '1' ? 'dark' : 'light';
      this.setStorageItem(this.storageKey, migrated);
      return migrated;
    }

    return null;
  }

  private persistPreference(preference: ThemePreference) {
    if (!this.isStorageAvailable()) return;
    this.setStorageItem(this.storageKey, preference);
    if (preference === 'dark' || preference === 'light') {
      this.setStorageItem(this.legacyDarkModeKey, preference === 'dark' ? '1' : '0');
    }
  }

  private getStorageItem(key: string): string | null {
    if (!this.storageAvailable) return null;
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private setStorageItem(key: string, value: string) {
    if (!this.storageAvailable) return;
    try {
      localStorage.setItem(key, value);
    } catch {
      // Ignore storage errors (e.g., private mode or quota issues)
    }
  }

  private isStorageAvailable() {
    return this.storageAvailable;
  }

  private checkStorageAvailable(): boolean {
    if (typeof localStorage === 'undefined') return false;
    try {
      const testKey = '__theme_storage_test__';
      localStorage.setItem(testKey, '1');
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  private getMediaQuery(): MediaQueryList | null {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return null;
    }
    return window.matchMedia('(prefers-color-scheme: dark)');
  }
}
